// AUTO-GENERATED by scripts/gen-guide-index.mjs — do not hand-edit.
// A light, client-safe index of every guide in generated.ts (steps dropped). Powers the
// route-aware help surfaces (GlobalHelp / GlobalTour). Re-run: node scripts/gen-guide-index.mjs

export interface GuideIndexEntry {
  id: string;
  title: string;
  description: string;
  route: string;
  audience: 'admin' | 'teacher' | 'family';
}

export const GUIDE_INDEX: GuideIndexEntry[] = [
  {
    id: 'si-print-show-runbook',
    title: 'Open a print-ready run-of-show runbook',
    description:
      'Generate a clean, printable runbook for a crew role — Stage Manager run sheet, MC script, cue sheets, or cast list.',
    route: '/summer-institute/show-script/print?instituteId=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-browse-song-examples',
    title: 'Browse recital song examples',
    description:
      'Explore ideas for your recital piece, grouped by genre, and search by title, show, artist, or genre — Session I or II.',
    route: '/summer-institute/song-examples',
    audience: 'family',
  },
  {
    id: 'si-family-sign-in',
    title: 'Sign in to the members portal',
    description:
      'How families open their Summer Institute portal — one tap from the emailed link, no password to remember.',
    route: '/summer-institute/sign-in',
    audience: 'family',
  },
  {
    id: 'withdraw-a-student',
    title: 'Withdraw a student',
    description:
      'Wind a student down cleanly — cancel registrations, release future lessons, finalize credits — on 30-day notice or immediately, with an optional refund.',
    route: '/students',
    audience: 'admin',
  },
  {
    id: 'offboard-a-teacher',
    title: 'Offboard a teacher',
    description:
      'Deactivate a departing teacher without orphaning a single student — reassign every tied student to a new teacher first, then revoke access and free their future slots.',
    route: '/teachers',
    audience: 'admin',
  },
  {
    id: 'deactivate-a-staff-member',
    title: 'Deactivate a staff member',
    description:
      'Revoke a staff member’s login immediately when they leave the organization — the platform can never lock out its last super admin.',
    route: '/staff-management',
    audience: 'admin',
  },
  {
    id: 'approve-placement',
    title: 'Approve a new student’s placement',
    description: 'Review a new inquiry and approve the teacher + times a family may book.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'generate-skip-code',
    title: 'Generate an intro skip code',
    description:
      'Let a specific student skip the required intro lesson and book recurring lessons directly.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'family-book-intro-lesson',
    title: 'Book your intro lesson',
    description:
      'Confirm the teacher we matched you with, pick from the times we approved, and pay the $40 intro-lesson fee.',
    route: '/prospect-portal?token=realvid_tok_1',
    audience: 'family',
  },
  {
    id: 'confirm-prospect',
    title: 'Confirm a prospect',
    description:
      'Approve a new inquiry — open the placement modal to choose the teacher and the times the family may book, then send them the invite.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'decline-archive-inquiry',
    title: 'Decline or archive an inquiry',
    description:
      'Remove an inquiry from the active pipeline by declining it (rejected) or archiving it away (filed, reversible).',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'approve-scheduling-request',
    title: 'Approve a scheduling request',
    description:
      'Approve a family’s requested lesson — this generates a Stripe payment link and emails it to them.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'send-payment-link',
    title: 'Send or resend a payment link',
    description:
      'Email the family the Stripe payment link again as a reminder — it does not create a new charge.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'mark-request-paid',
    title: 'Mark a request as paid',
    description:
      'Manually mark a sent-payment request paid to activate the student and auto-enroll the lesson — only after payment is confirmed.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'send-counter-proposal',
    title: 'Send a counter-proposal',
    description:
      'Propose an alternative teacher, day, or time to a family and email them the counter-offer, which expires in 7 days.',
    route: '/prospects',
    audience: 'admin',
  },
  {
    id: 'teacher-check-in-lesson',
    title: 'Check in and record a lesson',
    description:
      "Mark attendance for one of today's lessons and log what you covered — this drives pay and make-up credit.",
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-quick-check-in',
    title: 'Check in from a lesson card',
    description:
      'Use the "Submit Check-In" button on a lesson card to open the check-in form — pick an attendance status and add session notes.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-claim-group-class',
    title: 'Claim a group class',
    description:
      'Claim an unclaimed group class on your schedule so it counts as yours for check-in and pay.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-view-today',
    title: "View today's lessons",
    description:
      'See the lessons on your schedule for today with times, students, locations, and attendance.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-view-calendar',
    title: 'View my schedule calendar',
    description:
      'Browse your lessons in a month calendar and open any day to see details or schedule a make-up.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-view-upcoming',
    title: 'View upcoming lessons',
    description: 'Review your lessons for today and the next 30 days in a single list.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-send-lesson-link',
    title: 'Send a virtual lesson link',
    description:
      'Email (and text) the family the join link for a lesson so they can connect on time.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-join-virtual-lesson',
    title: 'Join a virtual lesson',
    description: 'Start or join the video room for a virtual lesson from your schedule.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-request-sub',
    title: 'Request a substitute for a lesson',
    description: "Ask admin to cover one of your upcoming lessons when you can't teach it.",
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-request-reschedule',
    title: 'Request a lesson reschedule',
    description: 'Propose a new date/time for an upcoming lesson for admin to review and confirm.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-book-makeup',
    title: 'Book a make-up lesson',
    description:
      "Schedule a make-up against a student's open credit into an available make-up slot.",
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-view-availability',
    title: 'View my availability and blackouts',
    description:
      'Review your approved weekly availability rules and any blackout dates you have on file.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-submit-availability',
    title: 'Submit availability for approval',
    description:
      'Send admin the days and time blocks you can teach so they can add them to the calendar.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-add-blackout',
    title: 'Add a blackout / time off',
    description: 'Mark dates you are unavailable so the scheduler stops booking lessons then.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-remove-blackout',
    title: 'Edit or remove a blackout',
    description: 'Update a blackout window or delete it to reopen those dates for booking.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-view-students',
    title: 'View my students and contacts',
    description:
      "Browse the students assigned to you and see each one's school, grade, and family contact info.",
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-send-material',
    title: 'Send materials to a student',
    description:
      'Share a link to sheet music, a recording, or practice material with a student and their family.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-message-family',
    title: 'Message a student’s family',
    description: "Send a message to a student's family and read their replies in the thread.",
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-music-library',
    title: 'Manage my music library',
    description: 'Upload pieces for a student so they can see them in their client portal.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-view-earnings',
    title: 'View my earnings and pay history',
    description:
      'Review year-to-date pay and drill into each pay period to see per-session earnings.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-notification-prefs',
    title: 'Manage my notification preferences',
    description:
      'Turn the pre-lesson prep ping on or off so you control the reminders the app sends you.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-connect-calendar',
    title: 'Connect my Google Calendar',
    description:
      'Link your Google Calendar so your CFVA lessons show up alongside your other events.',
    route: '/teacher-portal',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-overview',
    title: 'Start onboarding from my invite link',
    description:
      'Open the onboarding checklist from the emailed link and work through each step in order.',
    route:
      '/teacher-onboarding?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-profile',
    title: 'Complete my teacher profile',
    description: 'Fill in your name, contact info, bio, and headshot during onboarding.',
    route:
      '/teacher-onboarding/profile?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-availability',
    title: 'Submit my onboarding availability',
    description:
      'Provide your weekly teaching schedule during onboarding for admin to build your calendar.',
    route:
      '/teacher-onboarding/availability?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-w9',
    title: 'Submit my W-9',
    description:
      'Complete and e-sign your W-9 during onboarding so CFVA can pay you and issue a 1099.',
    route:
      '/teacher-onboarding/w9?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-payouts',
    title: 'Review how I get paid',
    description:
      'Read the payouts explainer — CFVA pays contractors via QuickBooks, so there is no setup in this app.',
    route:
      '/teacher-onboarding/direct-deposit?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-handbook',
    title: 'Acknowledge the teacher handbook',
    description:
      'Open and review the teacher handbook PDF, then agree and e-sign to acknowledge the CFVA teacher policies.',
    route:
      '/teacher-onboarding?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-onboarding-finalize',
    title: 'Finalize onboarding and get portal access',
    description:
      'Complete all onboarding steps to trigger account setup and the magic link that unlocks your portal.',
    route:
      '/teacher-onboarding?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'teacher-recertify-w9',
    title: 'Re-certify my W-9',
    description:
      'Re-submit your W-9 when admin flags a re-certification so your payments stay active.',
    route:
      '/teacher-onboarding/w9?token=a1b2c3d4e5f60718293a4b5c6d7e8f90a1b2c3d4e5f60718293a4b5c6d7e8f90',
    audience: 'teacher',
  },
  {
    id: 'family-submit-inquiry',
    title: 'Submit an inquiry',
    description:
      'Send CFVA a new-student inquiry from the public form so the team can match a teacher and email you a booking invitation.',
    route: '/inquiry',
    audience: 'family',
  },
  {
    id: 'family-request-recurring',
    title: 'Browse and request recurring lessons',
    description:
      'After your intro, choose a plan, teacher, day, and time and send a recurring-lesson request to the team for confirmation — no charge yet.',
    route: '/prospect-portal?token=realvid_tok_1',
    audience: 'family',
  },
  {
    id: 'family-placement-registration',
    title: 'Review the first-month total and register',
    description:
      'On the recurring-lesson confirm screen, review the monthly rate plus the one-time registration fee before sending your enrollment request.',
    route: '/prospect-portal?token=realvid_tok_1',
    audience: 'family',
  },
  {
    id: 'family-welcome-walkthrough',
    title: 'Complete the welcome walkthrough',
    description:
      'First-time setup: acknowledge studio policies, optionally sync your calendar, and set notification preferences before entering the portal.',
    route: '/welcome',
    audience: 'family',
  },
  {
    id: 'family-view-schedule',
    title: 'View your schedule and upcoming lessons',
    description:
      'See your next lesson and everything coming up, including virtual-lesson join links, from the family portal.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-book-makeup',
    title: 'Request a make-up lesson',
    description:
      'Use an earned make-up credit to request a lesson time. The studio reviews it before it’s confirmed — your credit stays Open and the time is held while it waits for approval.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-pay-invoices',
    title: 'View and pay invoices',
    description:
      'Review your payment history and open the secure Stripe billing portal to update your card, view invoices, or manage your subscription.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-view-materials',
    title: 'View materials and session notes',
    description:
      'Browse the sheet-music library and materials your teacher shared, filter by type, and read the notes left after each lesson.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-message-teacher',
    title: 'Message the CFVA team',
    description:
      'Send a message to the CFVA admin team from the portal and get replies in the same thread.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-cancel-lesson',
    title: 'Cancel an enrollment',
    description:
      'Request cancellation of a recurring enrollment, respecting the 30-day notice policy.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-request-new-lesson',
    title: 'Request a new lesson plan',
    description:
      'Choose an intro, drop-in, or monthly enrollment for your student. A monthly enrollment is submitted as a request and saves your card securely — nothing is charged until the studio approves it.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'family-track-requests',
    title: 'Track your lesson requests',
    description:
      'Every lesson you request — a make-up or a new plan — needs studio approval before it’s confirmed. The “Your Requests” card shows each one’s status so you always know where it stands.',
    route: '/client-portal',
    audience: 'family',
  },
  {
    id: 'setup-family-billing',
    title: "Set up a family's monthly billing",
    description:
      'Create the registration and Stripe subscription for a family, then optionally email them the card-setup link — nothing is charged until they enter a card.',
    route: '/families',
    audience: 'admin',
  },
  {
    id: 'catch-up-family',
    title: 'Send a family a catch-up charge link',
    description:
      'Generate a one-time Stripe link to collect overdue tuition — nothing is charged until the family opens it and pays.',
    route: '/families',
    audience: 'admin',
  },
  {
    id: 'send-family-onboarding-link',
    title: 'Send a family an onboarding / portal link',
    description: 'Email a family that has no login the link to set up their client-portal account.',
    route: '/families',
    audience: 'admin',
  },
  {
    id: 'approve-lesson-request',
    title: 'Approve or decline a lesson request',
    description:
      'Every lesson a family requests — a make-up or a new plan — waits for your approval. Review the pending request on the dashboard, then approve to confirm it or decline to release the held slot.',
    route: '/admin-operations-dashboard',
    audience: 'admin',
  },
  {
    id: 'mark-lesson-attendance',
    title: 'Mark attendance for a lesson',
    description:
      'Record whether a single lesson was attended, missed, or cancelled — which can fire a drop-in charge or create a makeup credit.',
    route: '/teacher-check-in-dashboard',
    audience: 'teacher',
  },
  {
    id: 'bulk-mark-attendance',
    title: 'Bulk-mark attendance',
    description:
      'Select several lessons at once and mark them present or issue makeup credits together.',
    route: '/teacher-check-in-dashboard',
    audience: 'admin',
  },
  {
    id: 'override-attendance',
    title: 'Override an attendance record',
    description:
      'Admin-correct a past attendance record; the system reconciles credits and this affects teacher pay.',
    route: '/teacher-check-in-dashboard',
    audience: 'admin',
  },
  {
    id: 'schedule-makeup-from-credit',
    title: 'Schedule a makeup using an open credit',
    description:
      "Book a makeup lesson against a family's open credit and optionally notify the family.",
    route: '/makeup-scheduling',
    audience: 'admin',
  },
  {
    id: 'void-credit',
    title: 'Void a credit',
    description: 'Cancel an open or scheduled makeup credit so it can no longer be redeemed.',
    route: '/credits',
    audience: 'admin',
  },
  {
    id: 'view-filter-credits',
    title: 'View & filter all credits',
    description:
      'Browse every credit across students, filter by status, and sort by age to spot ones nearing expiry.',
    route: '/credits',
    audience: 'admin',
  },
  {
    id: 'change-family-tier',
    title: "Change a family's tuition tier",
    description:
      'Move an active subscription to a different tier or lesson duration — Stripe migrates the subscription and prorates the difference on the next invoice.',
    route: '/families',
    audience: 'admin',
  },
  {
    id: 'manage-failed-payments',
    title: 'Manage failed payments',
    description:
      'Review the active dunning cases and run the dunning cycle that emails and texts families about failed charges.',
    route: '/payment-dunning',
    audience: 'admin',
  },
  {
    id: 'resolve-dunning-case',
    title: 'Resolve a dunning case',
    description:
      'Close out a failed-payment case by waiving the charge or marking it manually resolved.',
    route: '/payment-dunning',
    audience: 'admin',
  },
  {
    id: 'review-teacher-earnings',
    title: 'Review teacher earnings for a pay period',
    description:
      'Scope earnings to a biweekly period and review what CFVA owes each teacher. This screen never moves money — payouts run in QuickBooks.',
    route: '/teacher-payouts',
    audience: 'admin',
  },
  {
    id: 'export-teacher-earnings-quickbooks',
    title: 'Export teacher earnings for QuickBooks',
    description: "Download the period's earnings as a CSV to pay teachers in QuickBooks.",
    route: '/teacher-payouts',
    audience: 'admin',
  },
  {
    id: 'create-venue-payout-batch',
    title: 'Create a venue payout batch',
    description: "Group a venue's unpaid lesson charges into a batch ready for settlement.",
    route: '/admin/venue-payouts',
    audience: 'admin',
  },
  {
    id: 'mark-venue-batch-paid',
    title: 'Mark a venue batch as paid',
    description:
      'Record that a venue has been paid for a batch, capturing the method and reference.',
    route: '/admin/venue-payouts',
    audience: 'admin',
  },
  {
    id: 'update-tuition-tier-pricing',
    title: 'Update tuition tier pricing',
    description:
      "Change a tier's student, teacher, or venue rate and sync it to Stripe as a new price.",
    route: '/admin/tuition-pricing',
    audience: 'admin',
  },
  {
    id: 'update-fees-and-si-care-rates',
    title: 'Update one-time fees & SI care rates',
    description:
      'Change intro/registration fees and Summer Institute before/after-care rates that drive prospect and family forms.',
    route: '/admin/tuition-pricing',
    audience: 'admin',
  },
  {
    id: 'configure-venue-rates',
    title: 'Configure venue rates',
    description:
      'Set per-location lesson rates and pay details that accrue into the venue-payout queue.',
    route: '/admin/venue-rates',
    audience: 'admin',
  },
  {
    id: 'settings-edit-system-setting',
    title: 'Edit a system setting',
    description:
      'Change a platform-wide configuration value (credit rules, program parameters, email gates, feature toggles) and save the category.',
    route: '/settings',
    audience: 'admin',
  },
  {
    id: 'connect-google-calendar',
    title: 'Connect Google Calendar',
    description:
      'Link a Google account and pick the master-schedule and teacher-availability calendars lessons sync to.',
    route: '/settings',
    audience: 'admin',
  },
  {
    id: 'resync-google-calendar',
    title: 'Re-sync or purge the Google Calendar',
    description:
      'Patch every upcoming lesson onto Google Calendar, or purge and repopulate the master calendar to clear duplicates.',
    route: '/settings',
    audience: 'admin',
  },
  {
    id: 'create-discount-coupon',
    title: 'Create a discount coupon',
    description:
      'Create a percentage or fixed-amount coupon and sync it to Stripe so it can be applied to enrollments.',
    route: '/admin/discounts',
    audience: 'admin',
  },
  {
    id: 'manage-tuition-subscription',
    title: 'Activate, pause, or cancel a subscription',
    description:
      "Manage a family's Stripe tuition subscription — activate a pending registration, or pause/resume/cancel an active one.",
    route: '/admin/subscriptions',
    audience: 'admin',
  },
  {
    id: 'configure-communication-preferences',
    title: 'Configure notification channels & quiet hours',
    description:
      'Turn SMS/email notification types on or off system-wide and set retry, reminder-lead-time, and quiet-hour behavior.',
    route: '/communication-preferences',
    audience: 'admin',
  },
  {
    id: 'edit-notification-template',
    title: 'Edit an email or SMS template',
    description:
      'Change the body (and subject, for email) of a notification template using the merge variables, and save the override.',
    route: '/email-sms-templates',
    audience: 'admin',
  },
  {
    id: 'send-template-test',
    title: 'Send a template test',
    description:
      'Send a [TEST]-prefixed copy of a template to yourself with sample data to preview the real email or SMS.',
    route: '/email-sms-templates',
    audience: 'admin',
  },
  {
    id: 'approve-queued-email',
    title: 'Approve or suppress a queued email',
    description:
      'Review a sensitivity-flagged email held for manual approval and either send it or suppress it.',
    route: '/email-queue',
    audience: 'admin',
  },
  {
    id: 'compose-manual-email',
    title: 'Compose a manual email to families',
    description:
      'Write a one-off email, pick recipients from students/programs, attach files, and send it through Resend.',
    route: '/email-queue',
    audience: 'admin',
  },
  {
    id: 'start-video-meeting',
    title: 'Start an ad-hoc video meeting',
    description:
      'Create a LiveKit video room, add teacher/family/prospect invitees, and send them join links by email or SMS.',
    route: '/admin-operations-dashboard',
    audience: 'admin',
  },
  {
    id: 'end-video-meeting',
    title: 'End or resend a video meeting',
    description:
      'Resend a meeting invite to all recipients, or end the meeting to close the room and revoke join links.',
    route: '/admin-operations-dashboard',
    audience: 'admin',
  },
  {
    id: 'join-video-meeting',
    title: 'Join a video lesson or meeting',
    description:
      'Open a CFVA video room from an emailed/texted invite link and join the LiveKit call.',
    route: '/v/guidedemo_video_token_public_0001',
    audience: 'family',
  },
  {
    id: 'run-report-export-csv',
    title: 'Run a report and export CSV',
    description:
      'Pick a report tab, apply program/location filters, and download the filtered rows as a CSV.',
    route: '/reports',
    audience: 'admin',
  },
  {
    id: 'configure-financial-export',
    title: 'Configure & export the accounting mapping',
    description:
      'Map financial fields to your accounting columns/categories, then export the mapped data as CSV or PDF.',
    route: '/financial-export',
    audience: 'admin',
  },
  {
    id: 'export-tax-1099-list',
    title: 'Export the 1099 / tax-reporting list',
    description:
      'Review accrued teacher earnings against the $600 1099 threshold and W-9 status, then download the CSV.',
    route: '/tax-reporting',
    audience: 'admin',
  },
  {
    id: 'review-w9-submission',
    title: 'Review and approve a W-9 submission',
    description:
      'Compare a submitted W-9 against the teacher record and approve or reject it, which gates their 1099.',
    route: '/tax-reporting/w9-review',
    audience: 'admin',
  },
  {
    id: 'export-venue-usage',
    title: 'Export venue usage & amounts owed',
    description:
      'Scope venue lesson usage to a date range and export the billable hours and amounts owed per venue.',
    route: '/admin/venue-usage',
    audience: 'admin',
  },
  {
    id: 'search-audit-log',
    title: 'Search & export the audit log',
    description:
      'Search every logged action by actor, category, and date, then export the filtered events as CSV.',
    route: '/audit-log',
    audience: 'admin',
  },
  {
    id: 'resolve-scheduling-exception',
    title: 'Resolve a scheduling exception',
    description:
      'Clear a flagged lesson (no-show or exception-noted) by recording a resolution note.',
    route: '/exceptions',
    audience: 'admin',
  },
  {
    id: 'request-pitr-restore',
    title: 'Request a point-in-time database restore',
    description:
      'Log a PITR restore request for a target timestamp — a last-resort, destructive recovery action.',
    route: '/admin/backup-status',
    audience: 'admin',
  },
  {
    id: 'run-data-retention-job',
    title: 'Configure & run a data-retention job',
    description: 'Set archive/purge windows per data type and trigger an archive or purge run.',
    route: '/admin/data-retention',
    audience: 'admin',
  },
  {
    id: 'sign-in-magic-link',
    title: 'Sign in with a magic link or code',
    description: 'Sign in without a password using an emailed one-tap link plus a 6-digit code.',
    route: '/login',
    audience: 'family',
  },
  {
    id: 'reset-password',
    title: 'Reset your password',
    description: 'Request a password-reset email and set a new password from the secure link.',
    route: '/login',
    audience: 'family',
  },
  {
    id: 'set-up-totp-2fa',
    title: 'Set up two-factor authentication',
    description: 'Enable TOTP 2FA with an authenticator app and save the one-time recovery codes.',
    route: '/profile',
    audience: 'admin',
  },
  {
    id: 'complete-totp-challenge',
    title: 'Pass the two-factor challenge',
    description:
      'Verify your identity with a 6-digit code or a recovery code to continue into a protected area.',
    route: '/auth/totp-challenge',
    audience: 'admin',
  },
  {
    id: 'add-passkey',
    title: 'Add a passkey',
    description:
      'Register a device passkey (Face ID, Touch ID, Windows Hello, or a security key) for faster, phishing-resistant sign-in.',
    route: '/profile',
    audience: 'admin',
  },
  {
    id: 'school-portal-daily',
    title: 'Run the school lesson portal',
    description:
      "View today's on-site Arts 1:1 lessons and mark a student absent from a school's tokenized portal.",
    route: '/school/guidedemo_school_portal_token_0001',
    audience: 'admin',
  },
  {
    id: 'read-analytics-dashboard',
    title: 'Read the Analytics dashboard',
    description:
      'Understand program performance — check-in rate, active students, credit health, and attendance trends — and change the reporting window.',
    route: '/analytics',
    audience: 'admin',
  },
  {
    id: 'read-revenue-dashboard',
    title: 'Read the Revenue dashboard',
    description:
      'See the financial picture — revenue this month vs last, active subscriptions, average per student, outstanding balances, and the monthly trend.',
    route: '/revenue',
    audience: 'admin',
  },
  {
    id: 'read-lesson-ledger',
    title: 'Use the Lesson Ledger',
    description:
      'Drill into any teacher, student, or venue to see every check-in that paid a teacher or charged a family.',
    route: '/ledger',
    audience: 'admin',
  },
  {
    id: 'read-operational-metrics',
    title: 'Read the Operational Metrics dashboard',
    description:
      'Track the day-to-day operation — total check-ins, no-shows, system usage — filtered by window and venue.',
    route: '/operational-metrics',
    audience: 'admin',
  },
  {
    id: 'read-availability-calendar',
    title: 'Read the Availability Calendar',
    description:
      'See teacher availability, blackouts, and generated booking slots across the week, and switch between calendar, grid, and slot views.',
    route: '/availability-calendar',
    audience: 'admin',
  },
  {
    id: 'read-notifications-inbox',
    title: 'Review the notifications inbox',
    description:
      'See every email and SMS the system sent — filter by type, delivery status, and automated vs. manual — to confirm families got their messages.',
    route: '/notifications',
    audience: 'admin',
  },
  {
    id: 'use-onboarding-checklist',
    title: 'Work through the staff onboarding checklist',
    description:
      'Track your onboarding across role access, a system walkthrough, key procedures, people, and policies — your progress saves automatically.',
    route: '/onboarding-checklist',
    audience: 'admin',
  },
  {
    id: 'browse-hub-shared',
    title: 'Browse Hub shared contacts & files',
    description:
      'Search the Hub contact directory and file library from Studio, without exposing the integration secret to the browser.',
    route: '/hub-shared',
    audience: 'admin',
  },
  {
    id: 'use-resources-links',
    title: 'Find & share a resource link',
    description:
      'Every guide and shareable page in one place — copy a family-facing link to paste into an email, or open any internal page.',
    route: '/resources',
    audience: 'admin',
  },
  {
    id: 'read-bulk-audit-log',
    title: 'Read the Bulk Operations Audit Log',
    description:
      'A focused view of bulk actions only — mass credit grants, bulk check-ins, and CSV exports — so you can review anything that touched many records at once.',
    route: '/bulk-audit-log',
    audience: 'admin',
  },
  {
    id: 'read-system-logs',
    title: 'Read the System Logs',
    description:
      'The integration health panel — Stripe webhook events, email delivery, and SMS confirmations side by side — to diagnose why a payment, email, or text did or did not go through.',
    route: '/system-logs',
    audience: 'admin',
  },
  {
    id: 'read-lesson-action-log',
    title: 'Read the Lesson Action Log',
    description:
      'Every consequential lesson change — deleted lessons, counter-proposals, and bulk lesson actions — with the timestamp, actor, and the reason given.',
    route: '/lesson-action-log',
    audience: 'admin',
  },
  {
    id: 'read-sensitive-audit',
    title: 'Read the Sensitive Admin Audit',
    description:
      'The highest-scrutiny log — role edits, discounts, overrides, refunds, and permission changes — filtered from all admin actions. Super-admin / admin only.',
    route: '/sensitive-audit',
    audience: 'admin',
  },
  {
    id: 'read-admin-action-log',
    title: 'Read the Admin Action Log',
    description:
      'The full trail of dashboard actions — role changes, approvals, credits, bookings — with search, category, actor, and date filters, plus CSV export.',
    route: '/admin-action-log',
    audience: 'admin',
  },
  {
    id: 'si-add-scene',
    title: 'Add a run-of-show scene',
    description: 'Create a new scene, number, cue, transition, note, or break in the show order.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-edit-scene-body',
    title: "Edit a scene's script body",
    description:
      'Write the script / MC lines in the rich collaborative editor, co-editing live with other staff.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-submit-tech-element',
    title: 'Submit a technical element',
    description:
      "Fill in and mark a scene's lights, sound, mics, projection, or stage direction as ready.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-set-scene-owners',
    title: 'Set scene owners',
    description:
      'Assign the teachers responsible for a scene so they can edit its restricted fields.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-assign-students-scene',
    title: 'Assign students to a scene',
    description: 'Add the singers who appear in a scene so it shows on their devices.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-cast-student-inline',
    title: 'Cast a student to a character in a scene',
    description: 'Tie a singer already in a scene to the character they play there.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-set-scene-duration',
    title: 'Set scene duration',
    description: 'Record how long a scene runs so the show timing adds up.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-upload-scene-sheet',
    title: 'Upload sheet music to a scene',
    description: 'Attach a score PDF or image to a scene for rehearsal and the reading view.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-upload-scene-track',
    title: 'Upload a rehearsal track to a scene',
    description: 'Attach a backing or rehearsal audio track to a scene.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-upload-scene-choreo-video',
    title: 'Upload a choreography video',
    description: 'Attach a choreo reference video to a scene for students and the dance team.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-upload-scene-document',
    title: 'Upload a document (lyrics)',
    description: 'Attach a lyrics sheet or other reference document to a scene.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-mark-cue-called',
    title: 'Mark a cue as called',
    description: "On show night, tick a scene's cue as called so the run sheet stays live.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-set-choreo-notes',
    title: 'Set choreography notes',
    description:
      'Record formations, counts, and entrances for a scene, editable by the whole team.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-set-costume-note',
    title: "Set a scene's costume note",
    description: 'Record wardrobe requirements for a scene so students and staff see them.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-import-script',
    title: 'Import a Word or PDF script as scenes',
    description: 'Let the app parse a .docx or .pdf script into reviewable, editable scenes.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-import-lineup-scenes',
    title: 'Import the Friday lineup as scenes',
    description:
      'Pull the Friday show order into the run-of-show as scenes, skipping any already imported.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-manage-characters',
    title: 'Manage the cast of characters',
    description: "Add, rename, recolor, reorder, or remove the show's characters.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-filter-scenes',
    title: 'Filter scenes by type, owner, or people',
    description: 'Narrow the run-of-show to the scenes you want to rehearse or review.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-print-show-script',
    title: 'Print the show script',
    description:
      'Open a print-ready view — full script, run sheet, cast list, MC script, or cue sheets.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-export-annotated-pdfs',
    title: 'Export annotated script PDFs',
    description: "Open the marked-up song PDFs with every staff member's annotations baked in.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-open-reading-view',
    title: 'Open the reading view',
    description: 'Open the branded, read-only run-of-show view for rehearsal or the audience.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-save-snapshot',
    title: 'Save a show snapshot',
    description: 'Capture a named version of the whole show script you can return to later.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-revert-snapshot',
    title: 'Revert to a snapshot',
    description: 'Roll the entire show script back to a saved version.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-set-show-status',
    title: 'Set the show status',
    description:
      'Move the show between Draft, Final, and Locked — Locked freezes editing for non-directors.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-cast-by-scene',
    title: 'Cast students by scene',
    description: 'Drag students into character × scene cells on the casting grid.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-cast-by-character',
    title: 'Cast by character',
    description: 'Pick the people for a role and tick every scene it appears in at once.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-reveal-casting',
    title: 'Reveal or hide casting to students',
    description:
      'Publish named casting so students see their role, scenes, lines, and choreo — or hide it again.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-claim-song',
    title: 'Claim a song for a student',
    description: 'Assign an open song to a singer so nobody else takes it.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-assign-duet',
    title: 'Assign a duet',
    description: 'Pair a second student onto a song that a soloist already holds.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-release-song',
    title: 'Release a song',
    description: 'Unclaim a song from your student so it returns to the open pool.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-import-suggested-songs',
    title: 'Import suggested songs',
    description: 'Bulk-add a preset week list of repertoire to the session, skipping duplicates.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-add-custom-song',
    title: 'Add a custom song',
    description: 'Add a song that is not on the suggested list, optionally assigning it as you go.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-filter-songs',
    title: 'Filter songs',
    description: 'Narrow the board by search, voice part, open-only, or your own students.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-set-recital-song',
    title: "Set a student's recital song",
    description: 'Choose the recital song for a singer who does not have one yet.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-change-recital-song',
    title: "Change a student's song",
    description: 'Switch a singer to a different recital song.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-clear-recital-song',
    title: "Clear a student's song",
    description: "Release a singer's recital song back to the open pool.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-upload-recital-sheet',
    title: "Upload a student's sheet music",
    description: "Attach a score PDF to a singer's recital song so it becomes annotatable.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-upload-recital-track',
    title: "Upload a student's backing track",
    description: "Attach a backing track audio file to a singer's recital song.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-add-recital-notes',
    title: "Add notes to a student's song",
    description: "Record key, cuts, arrangement, and what to work on for a singer's song.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-mark-song-status',
    title: 'Mark song status',
    description:
      "Move a singer's song along its progress: Not picked, Picked, Rehearsing, Ready, Performed.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-start-voice-lesson',
    title: 'Start a voice lesson',
    description: 'Begin a 1:1 voice lesson with a student and run the live timer.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-end-voice-lesson',
    title: 'End a voice lesson',
    description: 'Stop the timer and mark the student as seen for the day.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-restart-voice-lesson',
    title: 'Re-see a student (restart a lesson)',
    description: 'Start a second lesson for a student already marked seen.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-pull-any-student-lesson',
    title: "Cover another teacher's student",
    description: 'Search for and start a lesson with any student, not just your own.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-view-my-lesson-progress',
    title: 'Check your lesson progress',
    description: "See how many of your students you've seen so far.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-director-lesson-tracking',
    title: 'Track lessons across all teachers',
    description: 'Watch live voice-lesson progress for every voice teacher (director).',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-schedule-add-block',
    title: 'Add a schedule block',
    description: 'Create a new time block on a day of the institute schedule.',
    route: '/admin/summer-institute',
    audience: 'admin',
  },
  {
    id: 'si-schedule-edit-block',
    title: 'Edit a schedule block',
    description: 'Change the time, room, audience, or details of an existing block.',
    route: '/admin/summer-institute',
    audience: 'admin',
  },
  {
    id: 'si-schedule-delete-block',
    title: 'Delete a schedule block',
    description: 'Permanently remove a block from the schedule.',
    route: '/admin/summer-institute',
    audience: 'admin',
  },
  {
    id: 'si-schedule-bulk-rooms',
    title: 'Bulk-assign rooms',
    description: 'Set the room/location on many blocks at once.',
    route: '/admin/summer-institute',
    audience: 'admin',
  },
  {
    id: 'si-schedule-notify-changes',
    title: 'Notify people of a schedule change',
    description: 'Send a push/bell to teachers or an email to families about updates.',
    route: '/admin/summer-institute',
    audience: 'admin',
  },
  {
    id: 'si-send-kudo',
    title: 'Send a kudo to a student',
    description: 'Recognize a student — it lights up on their iPad with confetti.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-view-kudo-feed',
    title: 'View the live kudo feed',
    description: 'Watch recent kudos stream in as staff send them.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-view-camp-goal',
    title: 'View & set the Camp Goal',
    description: 'See the shared kudo target the whole camp fills together.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-open-kudos-wall',
    title: 'Open the celebration wall',
    description: 'Launch the full-screen kudos wall for a display screen.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-print-certificates',
    title: 'Print kudo certificates',
    description: 'Open the printable recognition certificates for students.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-filter-needs-recognition',
    title: 'Find students who still need recognition',
    description: "Filter the roster to students who've received zero kudos.",
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-mark-attendance',
    title: "Mark a student's attendance",
    description: 'Set present / late / absent / excused for a student on a day.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-bulk-mark-present',
    title: 'Mark everyone present',
    description: 'Bulk-mark all unmarked students present for the day.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-checkin-search',
    title: 'Filter the check-in roster',
    description: 'Narrow the roster by name, voice part, or PIN.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-checkin-week-view',
    title: 'Switch to the week view',
    description: 'See a week-long attendance heatmap and export it as CSV.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-lineup-reorder',
    title: 'Reorder the Friday lineup',
    description: 'Drag songs to set the recital running order the pianist follows.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-lineup-assign',
    title: 'Assign a singer to a performance',
    description: 'Place an unassigned, picked song into a Friday performance.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-lineup-unassign',
    title: 'Unassign a singer from a performance',
    description: 'Pull a song out of a performance back to Unassigned.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-lineup-set-status',
    title: "Update a song's rehearsal status",
    description: 'Move a song along from Picked to Rehearsing / Ready / Performed.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-lineup-print-program',
    title: 'Print the Friday program',
    description: 'Open the printable family-facing program for the recital.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-browse-music-library',
    title: 'Browse the music library',
    description: 'Search the shared repertoire catalog for the institute.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-open-score-reader',
    title: 'Open a score in the reader',
    description: 'Launch the collaborative full-screen score reader for a piece.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-repertoire-toggle-lineup',
    title: "Set today's rehearsal lineup",
    description: 'Pull pieces into the "Today\'s rehearsal lineup" strip.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-accompanist-view-lineup',
    title: 'View the accompanist show order',
    description: 'See the recital scores in performance order.',
    route: '/summer-institute/staff/accompanist?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-accompanist-upload-copy',
    title: 'Upload your own copy of a score',
    description: "Attach your personal PDF for a piece, overriding the teacher's sheet.",
    route: '/summer-institute/staff/accompanist?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-accompanist-remove-copy',
    title: 'Remove your custom copy',
    description: "Delete your personal PDF and fall back to the teacher's sheet music.",
    route: '/summer-institute/staff/accompanist?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-accompanist-annotate',
    title: 'Annotate a lineup score',
    description: 'Mark up a recital score before the show.',
    route: '/summer-institute/staff/accompanist?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-accompanist-perform-mode',
    title: 'Enter perform mode day-of',
    description: 'Step through the running order in the locked full-screen reader.',
    route: '/summer-institute/staff/accompanist?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'teacher',
  },
  {
    id: 'si-send-direct-message',
    title: 'Send a direct message',
    description: 'Start a 1:1 conversation with a staff member or student.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-reply-in-thread',
    title: 'Read & reply in a thread',
    description: 'Open a conversation and respond.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-mark-message-read',
    title: 'Read a broadcast',
    description: 'Open an announcement to clear its unread flag.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-send-broadcast',
    title: 'Send a broadcast',
    description: 'Send an announcement to a group of staff, students, or families.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-messages-oversight',
    title: 'Turn on director oversight',
    description: 'Read every staff/student thread in the session read-only.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-switch-session',
    title: 'Switch between sessions',
    description: 'Move between institute weeks in the staff workspace.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-volunteer-pair-access',
    title: 'Sign in with a volunteer pair code',
    description: 'Give a volunteer intern access to the staff workspace with no login.',
    route: '/summer-institute/staff-pair',
    audience: 'admin',
  },
  {
    id: 'si-family-browse-portal',
    title: 'Browse the members portal',
    description:
      'Open your Summer Institute members portal (the emailed one-tap link) and use the tabs.',
    route:
      '/api/summer-institute/pair?token=guidedemofamilypair001&next=%2Fsummer-institute%2Fmembers',
    audience: 'family',
  },
  {
    id: 'si-family-view-song',
    title: "View your singer's recital song",
    description:
      'See the song your child chose with their voice teacher, plus its backing track and sheet music.',
    route:
      '/api/summer-institute/pair?token=guidedemofamilypair001&next=%2Fsummer-institute%2Fmembers',
    audience: 'family',
  },
  {
    id: 'si-student-home',
    title: 'Find your way around your student home',
    description: 'The iPad home shows your song, kudos, schedule, and links to everything else.',
    route: '/p/guidedemostudentpair01',
    audience: 'family',
  },
  {
    id: 'si-student-view-schedule',
    title: 'View your schedule',
    description: "See what's happening now and next, and open your full week day by day.",
    route: '/p/guidedemostudentpair01?next=%2Fsummer-institute%2Fstudent%2Fschedule',
    audience: 'family',
  },
  {
    id: 'si-student-tracks',
    title: 'Play your recital backing track',
    description: 'Rehearse your recital song from the My Song card on the iPad.',
    route: '/p/guidedemostudentpair01',
    audience: 'family',
  },
  {
    id: 'si-student-browse-songs',
    title: 'Browse suggested recital songs',
    description:
      'Browse the available recital songs before your voice teacher sets yours — see which are open, taken, or yours.',
    route: '/p/guidedemostudentpair01?next=%2Fsummer-institute%2Fstudent%2Fsongs',
    audience: 'family',
  },
  {
    id: 'si-student-choreography',
    title: 'Read choreography notes',
    description:
      'Read the choreography notes and watch practice videos for each number you perform in.',
    route: '/p/guidedemostudentpair01?next=%2Fsummer-institute%2Fstudent%2Fchoreography',
    audience: 'family',
  },
  {
    id: 'si-student-kudos',
    title: 'View your Kudos',
    description:
      "See the badges, level, and streak you've earned from staff recognition this week.",
    route: '/p/guidedemostudentpair01?next=%2Fsummer-institute%2Fstudent%2Fkudos',
    audience: 'family',
  },
  {
    id: 'si-student-message-staff',
    title: 'Message staff',
    description:
      'Read announcements and send messages to your teacher and directors from your iPad.',
    route: '/p/guidedemostudentpair01?next=%2Fsummer-institute%2Fstudent%2Fmessages',
    audience: 'family',
  },
  {
    id: 'handle-closure-lessons',
    title: 'Cancel or reschedule lessons on a closure',
    description:
      'Bulk-cancel or auto-reschedule all pending lessons that fall on a school-closure date.',
    route: '/holiday-calendar',
    audience: 'admin',
  },
  {
    id: 'si-open-annotated-export',
    title: 'Export the annotated script PDFs',
    description:
      "Open the marked-up song PDFs with every staff member's annotations baked on, ready to print or save as one PDF.",
    route:
      '/summer-institute/show-script/annotated?instituteId=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-pair-ipad-kiosk',
    title: 'Pair a student iPad (kiosk)',
    description:
      'Sign a shared iPad into a student’s portal by entering the pairing code the teacher shows — no camera, no password.',
    route: '/pair',
    audience: 'admin',
  },
  {
    id: 'si-read-kudos-wall',
    title: 'Read the Kudos Wall display',
    description:
      'What the live celebration wall shows on a lobby TV — the real-time kudo feed, the collective Camp Goal, and level-up moments.',
    route: '/summer-institute/wall?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'create-manage-quick-reply',
    title: 'Create a support quick reply',
    description:
      'Save a reusable canned response (substitution, payment, family, or general) to copy into support threads.',
    route: '/quick-replies',
    audience: 'admin',
  },
  {
    id: 'add-school-closure',
    title: 'Add a school closure',
    description:
      'Record a holiday or closure date on the studio calendar so its lessons can be handled.',
    route: '/holiday-calendar',
    audience: 'admin',
  },
  {
    id: 'create-manage-school',
    title: 'Create or edit a school',
    description:
      'Add a school (name, county, type, contacts) or edit one inline; deletion is super-admin only.',
    route: '/schools',
    audience: 'admin',
  },
  {
    id: 'si-family-register',
    title: 'Register for the Summer Institute',
    description:
      'Submit the public SI intake form for a student and land on the confirmation (or waitlist) screen.',
    route: '/summer-institute/register',
    audience: 'family',
  },
  {
    id: 'si-family-scholarship',
    title: 'Apply for an SI scholarship',
    description: 'Complete the essay-based scholarship application for a student.',
    route: '/summer-institute/scholarship',
    audience: 'family',
  },
  {
    id: 'si-family-care-request',
    title: 'Request before/after care',
    description:
      'Request extended-day care for a student, choose a rate plan, and pay by card or arrange another method.',
    route: '/summer-institute/before-after-care',
    audience: 'family',
  },
  {
    id: 'si-family-photo-order',
    title: 'Order Summer Institute photos',
    description:
      'Order a photo/video package for a student and pay by card or arrange in-person payment.',
    route: '/summer-institute/photo-order',
    audience: 'family',
  },
  {
    id: 'si-open-script-reading-view',
    title: 'Read the show script in reading mode',
    description:
      'Open the branded reading environment for the run-of-show and switch lenses — Director, Stage Manager, Cast Sides, Cue Sheet, and more.',
    route: '/summer-institute/show-script/read?instituteId=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
  {
    id: 'si-clear-attendance',
    title: 'Clear an attendance mark',
    description: 'Remove a status you set by mistake.',
    route: '/summer-institute/staff?institute=a9033999-f22e-4f60-bd8d-4cd47e0fc314',
    audience: 'admin',
  },
];

/**
 * Guides whose route matches (or is a prefix of) the given pathname, optionally filtered by
 * audience. Best match (longest matching route) first, capped so the help panel stays short.
 */
export function guidesForRoute(pathname: string, audience?: string): GuideIndexEntry[] {
  if (!pathname) return [];
  return GUIDE_INDEX.filter((g) => {
    if (audience && g.audience !== audience) return false;
    return pathname === g.route || pathname.startsWith(g.route + '/');
  })
    .sort((a, b) => b.route.length - a.route.length)
    .slice(0, 6);
}
